import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Mail, Edit2, Save, Eye, RotateCcw, FileText, 
  Shield, KeyRound, Bell, Users, DollarSign, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

// Template icons based on ID
const templateIcons = {
  'password_reset': KeyRound,
  'two_factor_code': Shield,
  'new_referral_notification': Bell,
  'referral_status_update': Users,
  'agent_statement': DollarSign,
};

export default function EmailTemplates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState({ subject: '', body: '' });
  const [editData, setEditData] = useState({ subject: '', body: '' });
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchTemplates();
    }
  }, [isAdmin]);

  const fetchTemplates = async () => {
    setLoading(true);
    const token = getToken();
    try {
      const response = await axios.get(`${API}/api/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setEditData({
      subject: template.subject,
      body: template.body
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    const token = getToken();
    
    try {
      await axios.put(
        `${API}/api/email-templates/${selectedTemplate.id}`,
        editData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template updated successfully');
      setShowEditModal(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (template) => {
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API}/api/email-templates/${template.id}/preview`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewHtml(response.data);
      setSelectedTemplate(template);
      setShowPreviewModal(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const handleReset = async (template) => {
    if (!window.confirm(`Reset "${template.name}" to default? This will discard your customizations.`)) {
      return;
    }
    
    const token = getToken();
    
    try {
      await axios.post(
        `${API}/api/email-templates/${template.id}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template reset to default');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="p-8 text-center">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">Access Denied</h3>
          <p className="text-slate-500">Only administrators can manage email templates</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="email-templates-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="w-7 h-7" />
            Email Templates
          </h1>
          <p className="text-slate-500">Customize the content of system emails</p>
        </div>
        <Button variant="outline" onClick={fetchTemplates}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Templates Grid */}
      <div className="grid gap-4">
        {templates.map((template) => {
          const TemplateIcon = templateIcons[template.id] || Mail;
          
          return (
            <Card key={template.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <TemplateIcon className="w-6 h-6 text-slate-600" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                          {template.name}
                          {template.is_default ? (
                            <Badge variant="secondary" className="text-xs">Default</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800 text-xs">Customized</Badge>
                          )}
                        </h3>
                        <p className="text-sm text-slate-500 mt-0.5">{template.description}</p>
                        <p className="text-sm text-slate-700 mt-2">
                          <strong>Subject:</strong> {template.subject}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {template.variables.map((variable) => (
                            <Badge key={variable} variant="outline" className="text-xs font-mono">
                              {`{{${variable}}}`}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreview(template)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleEdit(template)}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        {!template.is_default && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReset(template)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-template-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5" />
              Edit Template: {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subject Line</Label>
              <Input
                value={editData.subject}
                onChange={(e) => setEditData({ ...editData, subject: e.target.value })}
                placeholder="Email subject"
                data-testid="template-subject-input"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Email Body (HTML)</Label>
                <div className="flex flex-wrap gap-1">
                  {selectedTemplate?.variables.map((variable) => (
                    <button
                      key={variable}
                      type="button"
                      onClick={() => {
                        const textarea = document.getElementById('template-body');
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const newValue = editData.body.substring(0, start) + `{{${variable}}}` + editData.body.substring(end);
                          setEditData({ ...editData, body: newValue });
                        }
                      }}
                      className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded font-mono transition-colors"
                    >
                      {`{{${variable}}}`}
                    </button>
                  ))}
                </div>
              </div>
              <Textarea
                id="template-body"
                value={editData.body}
                onChange={(e) => setEditData({ ...editData, body: e.target.value })}
                placeholder="HTML content..."
                className="min-h-[300px] font-mono text-sm"
                data-testid="template-body-input"
              />
              <p className="text-xs text-slate-500">
                Use the variable buttons above to insert dynamic content.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="preview-template-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Preview: {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="preview" className="mt-4">
            <TabsList>
              <TabsTrigger value="preview">Preview</TabsTrigger>
              <TabsTrigger value="html">HTML Source</TabsTrigger>
            </TabsList>
            
            <TabsContent value="preview" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-4 py-2 border-b">
                  <p className="text-sm">
                    <strong>Subject:</strong> {previewHtml.subject}
                  </p>
                </div>
                <div 
                  className="p-4 bg-white"
                  dangerouslySetInnerHTML={{ __html: previewHtml.body }}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="html" className="mt-4">
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-auto max-h-[400px] text-sm">
                {previewHtml.body}
              </pre>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
